import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { v4 as uuidv4 } from 'uuid';

function itemsFromTemplate(checklistTemplate = []) {
    return checklistTemplate.map(point => ({
        pointUuid: point.uuidv4,
        title: point.title,
        ok: false,
        note: '',
    }));
}

@observer
class ClubOpsRangeInspectionDrawer extends Component {
    constructor(props) {
        super(props);
        const { installation = {} } = props;
        this.state = {
            items: itemsFromTemplate(installation.checklistTemplate),
            defects: [],
            score: '',
            description: '',
            newDefectTitle: '',
            error: '',
            isSaving: false,
        };
    }

    onField = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, error: '' });
    }

    toggleItem = (pointUuid) => {
        this.setState(prev => ({
            items: prev.items.map(item => (item.pointUuid === pointUuid ? { ...item, ok: !item.ok } : item)),
        }));
    }

    onItemNote = (pointUuid, value) => {
        this.setState(prev => ({
            items: prev.items.map(item => (item.pointUuid === pointUuid ? { ...item, note: value } : item)),
        }));
    }

    addDefect = () => {
        const { newDefectTitle, defects } = this.state;
        const title = newDefectTitle.trim();
        if (!title) {
            return;
        }
        const defect = {
            uuidv4: uuidv4(),
            title,
            note: '',
            status: 'open',
            createdDate: new Date().toISOString(),
        };
        this.setState({ defects: [...defects, defect], newDefectTitle: '' });
    }

    removeDefect = (defectUuid) => {
        this.setState(prev => ({
            defects: prev.defects.filter(d => d.uuidv4 !== defectUuid),
        }));
    }

    save = async () => {
        const { appState } = this.props.stores;
        const { installation, callback = () => {}, drawerLevel = 1 } = this.props;
        const {
            items, defects, score, description,
        } = this.state;
        this.setState({ isSaving: true, error: '' });
        const response = await util.fetchApi('/api/inspections/', { method: 'POST' }, {
            installation: installation.id, items, defects, score, description,
        });
        if (response?.status === 201) {
            callback();
            appState.toggleDrawer(false, drawerLevel);
            return;
        }
        this.setState({ isSaving: false, error: response?.message || 'Could not save' });
    }

    render() {
        const { installation = {} } = this.props;
        const {
            items, defects, score, description, newDefectTitle, error, isSaving,
        } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 720px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='px-3'>
                        <h5 class='mb-3'>
                            <i class='fa-duotone fa-clipboard-check me-2' />
                            <Text id='clubOps.inspection-title'>New range inspection</Text>
                            {!!installation.name && <span class='text-body-secondary ms-2'>{installation.name}</span>}
                        </h5>

                        <div class='mb-3'>
                            <label class='form-label'><Text id='clubOps.inspection-checklist'>Checklist</Text></label>
                            {items.length === 0 && (
                                <div class='text-body-secondary py-2'>
                                    <Text id='clubOps.inspection-no-checklist'>This range has no checklist yet.</Text>
                                </div>
                            )}
                            {items.map(item => (
                                <div key={item.pointUuid} class='d-flex flex-wrap align-items-center gap-2 border-bottom py-2'>
                                    <div class='form-check flex-grow-1'>
                                        <input
                                            class='form-check-input item-check'
                                            type='checkbox'
                                            id={`inspectionItem-${item.pointUuid}`}
                                            checked={item.ok}
                                            onChange={() => this.toggleItem(item.pointUuid)}
                                        />
                                        <label class='form-check-label' for={`inspectionItem-${item.pointUuid}`}>
                                            {item.title}
                                        </label>
                                    </div>
                                    <input
                                        class='form-control form-control-sm'
                                        style='max-width: 220px;'
                                        type='text'
                                        placeholder='Note'
                                        value={item.note}
                                        onInput={e => this.onItemNote(item.pointUuid, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>

                        <div class='mb-3'>
                            <label class='form-label'><Text id='clubOps.inspection-defects'>Defects</Text></label>
                            {defects.length > 0 && (
                                <ul class='list-group mb-2'>
                                    {defects.map(defect => (
                                        <li key={defect.uuidv4} class='list-group-item d-flex justify-content-between align-items-center'>
                                            <span>{defect.title}</span>
                                            <button type='button' class='btn btn-sm btn-outline-danger rounded-pill' onClick={() => this.removeDefect(defect.uuidv4)}>
                                                <Text id='clubOps.inspection-remove-defect'>Remove</Text>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div class='d-flex gap-2'>
                                <input
                                    class='form-control'
                                    type='text'
                                    name='newDefectTitle'
                                    placeholder='Løs skrue, ødelagt lys, ...'
                                    value={newDefectTitle}
                                    onInput={this.onField}
                                />
                                <button type='button' class='btn btn-outline-primary rounded-pill btn-add-defect' onClick={this.addDefect}>
                                    <i class='fa-solid fa-plus me-1' />
                                    <Text id='clubOps.inspection-add-defect'>Add defect</Text>
                                </button>
                            </div>
                        </div>

                        <div class='row'>
                            <div class='col-4 mb-3'>
                                <label class='form-label'><Text id='clubOps.inspection-score'>Score</Text></label>
                                <input class='form-control' type='number' name='score' value={score} onInput={this.onField} />
                            </div>
                            <div class='col-8 mb-3'>
                                <label class='form-label'><Text id='clubOps.inspection-description'>Description</Text></label>
                                <input class='form-control' type='text' name='description' value={description} onInput={this.onField} />
                            </div>
                        </div>

                        {!!error && <div class='alert alert-danger py-2'>{error}</div>}

                        <div class='d-flex justify-content-end'>
                            <button type='button' class='btn btn-primary rounded-pill' disabled={isSaving} onClick={this.save}>
                                {!!isSaving && <span class='spinner-border spinner-border-sm me-2' />}
                                <i class='fa-solid fa-save me-1' />
                                <Text id='clubOps.inspection-save'>Save</Text>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ClubOpsRangeInspectionDrawer;
